/**
 * GARDEN DECOR — bespoke scenery objects (rocks, stumps, garden objects) the player BUYS from the
 * garden-scene decor shop and PLACES freely in the iso garden. Unlike placed flowers (which are backed
 * by harvest inventory), decor is a pure cosmetic purchase: buying adds to an OWNED count, and placing
 * consumes one from that count (removing/pickup returns it). Each placed instance can be moved.
 *
 * The art for each item is drawn procedurally in the render layer (see GardenGroundLayer.drawDecor),
 * keyed by `DecorId`; this module owns the catalogue (id, name, price, category, collision footprint).
 */
import { screenToIso } from "../render/iso";
import { DECOR_ART_SCALE } from "../render/artScale";   // plain numbers — never import the art file from sim (it drags Pixi into every page)
import { tuning, onTuning } from "./tuningClient";
import { effectiveDecorPrice } from "./decorPricing";
import { isIn, isPreview, type ReleaseId } from "./releases";

/** The screen-px-per-unit the garden bakes decor at (must match GardenGroundLayer.DECOR_S / decorArt DECOR_DRAW_S). */
export const DECOR_S = 74;

export type DecorId =
  // rocks & boulders
  | "pebbles" | "rock" | "boulder" | "mossyRock"
  // wood & stumps
  | "stump" | "log"
  // garden objects (fences are their own grid-edge system now, not a decor item — see sim/fence.ts)
  | "lantern" | "signpost" | "birdBath"
  // planting HOSTS — hold a plant so trailing vines drape/climb over an edge (like the greenhouse pot)
  | "planterBox" | "hangingBasket" | "stoneUrn" | "windowBox" | "terracottaPots" | "wallTrough"
  // TREES — big scenery trees (the "oak" matches the default scene tree; sized off it as reference)
  | "oakTree" | "pineTree" | "birchTree" | "mapleTree"
  // more ROCKS / nature
  | "rockCairn"
  // more WOOD
  | "birdhouse" | "woodpile"
  // more OBJECTS (ornaments)
  | "sundial" | "wheelbarrow" | "pinwheel"
  | "wateringCan" | "gardenBench" | "torch" | "obelisk"
  | "fountain"
  // ACHIEVEMENT REWARDS (earned first, then buyable — see DecorDef.reward) — the round-2 ornaments and the brazier family
  | "carvedOrb" | "twinLampPost" | "stoneLantern" | "crystalObelisk" | "windSpinner"
  | "brazierSpectral" | "brazierFrost" | "brazierAmethyst" | "brazierEmber" | "brazierGilt"
  // batch 3 — more trees / rocks / wood / objects
  | "cherryTree" | "poplarTree" | "willowTree"
  | "flatBoulder"
  | "gardenArch" | "barrel"
  | "windChime" | "armillary" | "wishingWell"
  | "scarecrow" | "topiaryBall" | "topiarySpiral" | "hammock" | "fairyLights" | "rainBarrel"
  | "logStore" | "gazingBall" | "dovecote" | "beehive" | "coldFrame" | "waterPump" | "firePit" | "pottingBench"
  // REWARD-ONLY, PER-SPECIES (sim/speciesDecor.ts): each placed copy carries the plant/fish/insect it honours
  | "botanicalMarker" | "goldStatue"
  // KICKSTARTER BACKER pieces (`backer: true`): granted by a redeem code, never sold, never earnable
  | "foundersBrazier";

export type DecorCategory = "trees" | "rocks" | "wood" | "objects" | "planters";

export interface DecorDef {
  id: DecorId;
  name: string;
  /** the content release this belongs to (sim/releases.ts) — hidden and never granted until that release is on.
   *  Absent (everything today) = always available. */
  release?: ReleaseId;
  icon: string;                 // emoji for the shop chip
  category: DecorCategory;
  price: number;                // coins
  /** collision radius (garden units) around the base — others (plants + decor) must clear it. (Water
   *  collision is separate: it's DERIVED FROM THE ITEM'S DRAWN SHADOW — see decorShadowClearance.) */
  footprint: number;
  blurb: string;
  /**
   * AN ACHIEVEMENT REWARD — EARNED FIRST, then merchandise. Set true and the item is grantable by the point
   * track (and by an achievement), and the coin shop hides it until that grant has opened it ("decor:<id>",
   * sim/cosmetics.ts decorOpen); from then on the shop sells more copies at `price` like any decor. A reward
   * item that is also `perSpecies` is a TROPHY and is never sold at all (isTrophy).
   *
   * WHY THE FLAG LIVES ON THE CATALOGUE ENTRY rather than in a separate registry of reward items: "is this
   * buyable" and "what does it cost" are the same fact about the same object, and splitting them across two
   * files is how a decor item ends up purchasable AND granted, or listed in a reward picker after being
   * retired from the game. One record, one truth. `isRewardItem` / `shopDecorIds` read it; the reward editor
   * filters on it so a rung can only ever grant something marked here.
   *
   * `price` is a real price on a reward item: what the shop charges for each copy after the first, what the
   * item sells BACK for (via DECOR_REFUND_RATE), and the number the reward editor shows so an author can judge
   * whether a grant is proportionate. Only the trophies carry price 0 — never bought, never sold.
   */
  reward?: boolean;
  /**
   * A KICKSTARTER BACKER piece — given to the campaign's backers through a redeem code (a `decor` prize; see
   * sim/redeem.ts) and obtainable NO other way: never listed in the Market (not even once owned, unlike a
   * `reward` item), never sold back, never on the point track or an achievement (so never a Garden Club prize),
   * and never named by a fish or insect spawn condition. It places from the bag like any decor. `price` is 0 —
   * there is no price to pay or to refund. Every never-traded rule reads `isKeepsake`, which is this OR a
   * trophy, so a new backer piece needs nothing but the flag.
   */
  backer?: boolean;
  /** PER-SPECIES: every copy honours one plant, fish or insect (DecorPlacement.species), owned per species
   *  under a bag key "id:kind:species" (decorKey). The art draws the species' own picture into it. */
  perSpecies?: boolean;
  /** HOSTS only: this item can hold planted plant(s). `rim` = where the plant's base sits in the decor's
   *  LOCAL art px (relative to the node origin). A hosted plant keeps its NATURAL size — the same scale it
   *  has on the ground and while it is dragged. (Hosts used to carry a `plantScale` of 0.42–0.62 that shrank
   *  the plant to "suit" the vessel; a daisy that halved the moment it landed in an urn read as a bug.)
   *  `climb` = the plant grows UP the host (a trellis) rather than draping down (default false).
   *
   *  MULTI-SLOT HOSTS: `slots` (default 1) is how many plants the item holds side by side, and `slotDx` is
   *  the LOCAL-px spacing between neighbouring slots along the item's length. The slots are laid out
   *  SYMMETRICALLY ABOUT `rim`, so `rim` stays exactly what it always was — the centre of the planting —
   *  and a one-slot host's geometry does not move by a pixel. Slot i sits at
   *  `rim.x + (i - (slots-1)/2) * slotDx`, which is what `hostSlotX()` below computes; every consumer must
   *  go through that function rather than re-deriving it, because the render position, the insect landing
   *  spot and the occupancy test all have to agree on the same three points.
   *
   *  WHY COUNT + SPACING RATHER THAN A LIST OF RIM POINTS. Three literal points would let the art and the
   *  schema drift apart one slot at a time, and every single-slot host on the roster would need rewriting
   *  as a one-element array. More decisively: a row of planting holes in one long box IS evenly spaced —
   *  that is the thing the art draws — so a count and a pitch describe it exactly, and any future editor
   *  who widens the box changes one number instead of re-centring three. */
  host?: { rim: { x: number; y: number }; climb?: boolean; slots?: number; slotDx?: number };
  /** The item may NOT be mirrored by a right-click (see `DecorPlacement.flip`). Set on anything whose art
   *  reads wrong backwards — readable text above all: the signpost's words would print reversed. Everything
   *  else is left flippable on purpose, including items lit from the up-left: a mirrored item carries its
   *  lit side and its shadow across with it, and that is the accepted price of the feature. */
  noFlip?: boolean;
  /** COLLISION / OCCLUSION BAND — the solid part of the art that crawling insects collide with and that
   *  occludes fliers. Stored in **si-FRACTIONS** (si = DECOR_S × effective DECOR_ART_SCALE), measured as the
   *  HEIGHT above the base at (0,0) — so it's RESIZE-SAFE (scales with the art) and the shadow (at the base,
   *  y≈0) is excluded by construction. `profile` = half-widths (si-fractions) sampled evenly TOP→BOTTOM, so
   *  the band respects the art's real silhouette (a taper/flare/bump), not a plain rectangle. Absent ⇒ an
   *  auto-default is derived (see decorBand()), so every current + future item is handled. Authored in the
   *  standalone dev viewer's Collisions tab and pasted here. */
  band?: DecorBand;
}

/** A decor collision/occlusion band in si-fractions (height above the base; profile = half-widths top→bottom). */
export interface DecorBand {
  top: number;        // upper line height (si-fraction above base; larger = higher up the art)
  bottom: number;     // lower line height (si-fraction; ≥0 — never below the base, so the shadow is excluded)
  profile: number[];  // ≥2 half-widths (si-fractions), evenly spaced from `top` down to `bottom`
}
/** True if a decor id is a planting HOST (can hold a plant). */
export function isHost(id: DecorId): boolean { return !!DECOR[id]?.host; }

/** How many plants this host holds side by side (1 for every host but the long planter box). Safe on a
 *  non-host id (0), so callers can ask before they know. */
export function hostSlots(id: DecorId): number {
  const h = DECOR[id]?.host;
  if (!h) return 0;
  return Math.max(1, Math.round(h.slots ?? 1));
}

/** The LOCAL art-px x of planting slot `i` on this host. Slots are centred on `host.rim.x` and spaced
 *  `slotDx` apart, so a single-slot host returns `rim.x` exactly and nothing about the existing four
 *  planters moves. `i` is clamped, so a save that stored a slot index from a host that later lost slots
 *  lands on a real one instead of floating off the end of the box. */
export function hostSlotX(id: DecorId, i: number, flip = false): number {
  const h = DECOR[id]?.host;
  if (!h) return 0;
  const n = hostSlots(id);
  const k = Math.min(n - 1, Math.max(0, Math.round(i)));
  const x = n <= 1 ? h.rim.x : h.rim.x + (k - (n - 1) / 2) * (h.slotDx ?? 0);
  // a FLIPPED host is drawn at scale.x = -1 about its base, so its slots mirror with the art: slot 0 of the
  // long planter box moves to the right-hand end, exactly where the mirrored picture now puts that hole.
  return flip ? -x : x;
}

/** True if this item may be mirrored (right-click toggles `flip`). False for `noFlip` items — see DecorDef. */
export function canFlipDecor(id: DecorId): boolean { return !DECOR[id]?.noFlip; }

/** Is this placement DRAWN mirrored? The stored bit, vetoed by `noFlip` — so a hand-edited save (or an item
 *  that gains `noFlip` later) renders the right way round instead of with reversed lettering. Every consumer
 *  of `flip` (the renderer, the pick box, the host slots) reads it through here. */
export function decorFlipped(d: { decor: DecorId; flip?: boolean }): boolean {
  return d.flip === true && canFlipDecor(d.decor);
}

// Footprints (collision radius, garden units) are sized to the ~2× decor art (see DECOR_S in
// GardenGroundLayer) so the no-overlap ring matches the bigger drawing. (WATER collision is separate and
// derived from each item's drawn SHADOW — see decorShadowClearance in decorArt.ts.)
//
// RETHEMED IDS — eight keys no longer name what they draw. A decor id is what a save stores for every
// placed copy, so a retheme changes the art and the `name`, never the key. Each entry carries an `// art:`
// line; the full map, id → what it draws now:
//   gardenArch → weather vane        scarecrow → broken column     hammock  → wooden crate
//   dovecote   → hay bale            beehive   → trash bin         coldFrame → seed bag
//   wallTrough → flower hamper       terracottaPots → ONE pot (was a cluster of three)
//
// RETIRING AN ID (deleting a key here) DELETES EVERY OWNED COPY: coerceDecorOwned / coerceDecorPlacements
// drop ids this record no longer has, silently, on the next load. A retirement must ship a load migration in
// sim/save.ts coerce that REFUNDS or SWAPS the id first — the way liftFromRetiredHosts (garden.ts) returns a
// plant to the bag when its host stops being one. (A coin refund also has to get past the server's currency
// checks, so plan it with server/index.js sanitise, not just the client loader.)
export const DECOR: Record<DecorId, DecorDef> = {
  // ── rocks & boulders ──
  pebbles:  { id: "pebbles",  name: "Pebble Cluster", icon: "🪨", category: "rocks", price: 120,  footprint: 0.42, blurb: "A little scatter of smooth pebbles." },
  rock:     { id: "rock",     name: "Rock",           icon: "🪨", category: "rocks", price: 120, footprint: 0.56, blurb: "A solid, weathered stone." },
  mossyRock:{ id: "mossyRock",name: "Mossy Rock",     icon: "🪨", category: "rocks", price: 300, footprint: 0.6, blurb: "A weathered rock topped with soft moss." },
  boulder:  { id: "boulder",  name: "Boulder",        icon: "🪨", category: "rocks", price: 1400, footprint: 1.05, blurb: "A big, rugged boulder." },
  // ── wood & stumps ──
  stump:    { id: "stump",    name: "Tree Stump",     icon: "🪵", category: "wood",  price: 300, footprint: 0.72, blurb: "A cut stump with visible rings." },
  log:      { id: "log",      name: "Fallen Log",     icon: "🪵", category: "wood",  price: 1400, footprint: 0.95, blurb: "A mossy fallen log lying on its side." },
  // ── garden objects (footprints sized to the ×2 larger art) ──
  lantern:  { id: "lantern",  name: "Garden Lantern", icon: "🏮", category: "objects", price: 650, footprint: 0.65,blurb: "A standing lantern with a warm glow." },
  // noFlip: the board carries the player's own two lines of text, which a mirror would print backwards.
  signpost: { id: "signpost", name: "Signpost",       icon: "🪧", category: "objects", price: 120, footprint: 0.42, blurb: "A little wooden signpost.", noFlip: true },
  birdBath: { id: "birdBath", name: "Bird Bath",      icon: "⛲", category: "objects", price: 1400, footprint: 0.9,  blurb: "A stone bird bath with a shimmer of water." },
  // ── planting HOSTS (hold a plant; trailing vines drape/climb over the edge) ──
  // rim.y is in LOCAL decor-art px (s = DECOR_S 74 × the item's DECOR_ART_SCALE); it matches the soil line
  // each art function draws (planterBox ≈ -s·0.63, hangingBasket basket ≈ -s·1.02, stoneUrn lip ≈ -s·0.815,
  // windowBox soil ≈ -s·0.49, terracottaPots soil ≈ -s·0.77 on the axis). windowBox is the one MULTI-SLOT
  // host: its rim is the CENTRE of a row of three, spaced `slotDx` apart — see hostSlotX().
  // `wallTrough` used to be listed here; it was rethemed into a flower basket and is no longer a host —
  // see the note on its entry further down, which now sits under `objects`.
  planterBox:    { id: "planterBox",    name: "Planter Box",    icon: "🪴", category: "planters", price: 650, footprint: 0.7, blurb: "A raised wooden box. Plant a vine to spill over its rim.", host: { rim: { x: 0, y: -79 }, } },
  hangingBasket: { id: "hangingBasket", name: "Hanging Basket", icon: "🧺", category: "planters", price: 650, footprint: 0.5, blurb: "A basket on a hook post. Vines hang down from it.",       host: { rim: { x: 0, y: -143 }, } },
  stoneUrn:      { id: "stoneUrn",      name: "Stone Urn",      icon: "⚱️", category: "planters", price: 1400, footprint: 0.55, blurb: "A classic stone urn. Vines drape over its wide lip.",     host: { rim: { x: 0, y: -109 }, } },
  // RETHEMED, NOT RENAMED — same reason as `terracottaPots` and `wallTrough` below: the id is what a save
  // stores for every placed copy. This was a shallow sill trough HUNG ON IRON BRACKETS holding ONE plant;
  // it now STANDS ON ITS OWN LEGS and holds THREE side by side. It is the roster's only MULTI-SLOT host,
  // and `slots`/`slotDx` exist for it (see the DecorDef.host doc). rim stays the CENTRE of the planting, so
  // slot 1 of 3 sits exactly where the single plant used to; slots 0 and 2 sit ±slotDx from it.
  // slotDx = 55 local px against a mouth half-width of 83.5 (openIn = halfW·0.885 at s = 125.8), so the
  // outer two plants sit at ±55 — inside the timber at both ends with room for a plant's own spread, and
  // far enough apart that three plants read as a ROW and not as one clump with fringes.
  // y went -56 → -62: the bed rides higher now that the box stands on legs instead of hanging off brackets.
  windowBox:     { id: "windowBox",     name: "Long Planter Box", icon: "🪴", category: "planters", price: 650, footprint: 0.85, blurb: "A wooden planter on legs that holds three plants in a row.", host: { rim: { x: 0, y: -62 }, slots: 3, slotDx: 55 } },
  // RETHEMED, NOT RENAMED. This slot used to be a CLUSTER of three pots; it now draws a SINGLE one.
  // The KEY stays `terracottaPots` on purpose — a decor id is what a save stores for every placed copy,
  // so renaming it would orphan every existing placement. The HOST MOVED WITH THE ART: the rim used to
  // point at the off-axis hero pot of the group (x -25), and with one pot on the axis it is x 0. y went
  // -102 → -100 for the lone vessel's mouth (s·0.385 against the hero's s·0.325).
  // art: ONE terracotta pot — the id is the old three-pot cluster's; saves store ids, so it stays.
  terracottaPots:{ id: "terracottaPots",name: "Terracotta Pot", icon: "🏺", category: "planters", price: 650, footprint: 0.55, blurb: "A weathered clay pot, chipped at the rim. Plant it and let it spill over.", host: { rim: { x: 0, y: -100 }, } },
  // RETHEMED, NOT RENAMED. This slot used to be the Wall Trough; it now draws a woven PICNIC HAMPER
  // (squat box, hinged lid folded back, arched carrying handle) packed with cut flowers. It passed
  // through a round wicker basket on the way, which read as one more planter -- see the art header.
  // The KEY stays `wallTrough` on purpose — same reason as `gardenArch`, `scarecrow`, `hammock`,
  // `dovecote`, `beehive` and `coldFrame`: a decor id is what a save stores for every placed copy, so
  // renaming it would orphan every existing placement.
  //
  // IT IS NO LONGER A HOST, and that was the deliberate call. The trough had `host: { rim, plantScale }`,
  // so a player could plant in it. A host's interior volume belongs to the player's plant, which is drawn
  // OVER the decor art — and this item's whole identity is the bunch of blooms it draws itself, which
  // would stand in the same hole as theirs (the fault hangingBasket's header records and refuses). So the
  // `host` field is GONE and the item moves `planters` → `objects`: a basket of cut flowers is an
  // ornament, not a vessel you plant into, and leaving it in `planters` would advertise a slot it no
  // longer has. A plant saved IN one of these is LIFTED BACK INTO THE BAG on load (liftFromRetiredHosts in
  // garden.ts, called from save.ts). Without that it kept its `hostId` and its host's gx/gy, and both
  // readers of `host` fall back to ground placement — so it stood on the grass inside the hamper's
  // footprint, and a trailing plant stood on ground it is never allowed on. Restoring `host` was the
  // other option and was refused for the reason above. The footprint shrinks 0.62 → 0.42 with the subject: the trough
  // was a ~2 ft span and this is a hamper you carry in one hand. The 0.42 still fits the hamper: it is
  // wider across the mouth than the round basket was (0.39s against 0.30s half-width) but shorter, and
  // the recorded water-collision ellipse it has to sit inside has never moved.
  // art: picnic hamper of cut flowers — the id is the old Wall Trough's; saves store ids, so it stays.
  wallTrough:    { id: "wallTrough",    name: "Picnic Hamper",  icon: "💐", category: "objects", price: 1400, footprint: 0.42, blurb: "A woven picnic basket with its lid open, packed with cut flowers and leaves." },
  // ── TREES (big scenery; footprints sized to the wide trunk base, not the canopy, so plants can tuck
  //    under the leaves). The oak matches the default scene tree used as the size/style reference. ──
  oakTree:    { id: "oakTree",    name: "Oak Tree",    icon: "🌳", category: "trees", price: 7000,  footprint: 1.05,blurb: "A grand oak with a wide, round crown." },
  pineTree:   { id: "pineTree",   name: "Pine Tree",   icon: "🌲", category: "trees", price: 3000,  footprint: 0.78,blurb: "A tall conical evergreen with tiered boughs." },
  birchTree:  { id: "birchTree",  name: "Silver Birch",icon: "🌳", category: "trees", price: 3000,  footprint: 0.7, blurb: "A slender birch with a pale, dark-flecked trunk and airy leaves." },
  mapleTree:  { id: "mapleTree",  name: "Maple Tree",  icon: "🍁", category: "trees", price: 7000,  footprint: 1.05,blurb: "A broad maple ablaze in warm autumn reds and golds." },
  // ── more ROCKS / nature ──
  rockCairn:      { id: "rockCairn",      name: "Rock Cairn",      icon: "🪨", category: "rocks", price: 650, footprint: 0.62, blurb: "A carefully balanced stack of smooth stones." },
  // ── more WOOD ──
  birdhouse:      { id: "birdhouse",      name: "Birdhouse",       icon: "🐦", category: "wood",  price: 650, footprint: 0.42, blurb: "A little gabled birdhouse on a tall post." },
  woodpile:       { id: "woodpile",       name: "Log Pile",        icon: "🪵", category: "wood",  price: 300, footprint: 0.75,blurb: "A neat stack of split firewood." },
  // ── more OBJECTS (ornaments) ──
  sundial:     { id: "sundial",     name: "Sundial",        icon: "🕰️", category: "objects", price: 1400, footprint: 0.5,  blurb: "A bronze sundial on a carved stone base." },
  wheelbarrow: { id: "wheelbarrow", name: "Wheelbarrow",    icon: "🛒", category: "objects", price: 650, footprint: 0.85, blurb: "A rustic wooden wheelbarrow full of soil." },
  pinwheel:    { id: "pinwheel",    name: "Pinwheel",       icon: "🎡", category: "objects", price: 120,  footprint: 0.3, blurb: "A colorful spinning pinwheel on a stake." },
  wateringCan: { id: "wateringCan", name: "Watering Can",   icon: "🪣", category: "objects", price: 120, footprint: 0.42,blurb: "A galvanized watering can, left out on the lawn." },
  gardenBench: { id: "gardenBench", name: "Garden Bench",   icon: "🪑", category: "objects", price: 1400, footprint: 0.9, blurb: "A slatted wooden bench to sit and admire the garden." },
  torch:       { id: "torch",       name: "Garden Torch",   icon: "🔥", category: "objects", price: 300, footprint: 0.4,  blurb: "A bamboo tiki torch with a dancing flame." },
  obelisk:     { id: "obelisk",     name: "Stone Obelisk",  icon: "🗿", category: "objects", price: 1400, footprint: 0.5,  blurb: "A tapering carved stone obelisk." },
  fountain:    { id: "fountain",    name: "Tiered Fountain", icon: "⛲", category: "objects", price: 7000, footprint: 1.0, blurb: "A grand two-tier stone fountain with cascading water." },
  // ── ACHIEVEMENT REWARDS — earned first (`reward: true` hides them until granted), then sold at these prices ──
  carvedOrb:      { id: "carvedOrb",      name: "Carved Stone Orb",   icon: "🌀", category: "objects", price: 650, reward: true, footprint: 0.5,  blurb: "A carved stone ball with a deep spiral and moss creeping up one side." },
  twinLampPost:   { id: "twinLampPost",   name: "Twin Lamp Post",     icon: "🏮", category: "objects", price: 1400, reward: true, footprint: 0.5,  blurb: "A cast-iron post with two curved arms and a lantern hanging from each." },
  stoneLantern:   { id: "stoneLantern",   name: "Stone Lantern",      icon: "🏯", category: "objects", price: 1400, reward: true, footprint: 0.6,  blurb: "A carved Japanese stone lantern with a warm glow." },
  crystalObelisk: { id: "crystalObelisk", name: "Crystal Obelisk",    icon: "💎", category: "objects", price: 7000, reward: true, footprint: 0.55, blurb: "A spire of pale crystal rising from the ground, giving off a cold light." },
  windSpinner:    { id: "windSpinner",    name: "Copper Wind Spinner", icon: "🌀", category: "objects", price: 650, reward: true, footprint: 0.35, blurb: "Two copper spirals that turn in the wind on a pole with a gold ball on top." },
  brazierSpectral:{ id: "brazierSpectral",name: "Spectral Brazier",   icon: "🔥", category: "objects", price: 3000, reward: true, footprint: 0.55, blurb: "A stone bowl on a pedestal with a cold green flame." },
  brazierFrost:   { id: "brazierFrost",   name: "Frost Brazier",      icon: "🔥", category: "objects", price: 1400, reward: true, footprint: 0.55, blurb: "A stone brazier burning pale, icy blue." },
  brazierAmethyst:{ id: "brazierAmethyst",name: "Amethyst Brazier",   icon: "🔥", category: "objects", price: 1400, reward: true, footprint: 0.55, blurb: "A pedestal bowl of violet fire." },
  brazierEmber:   { id: "brazierEmber",   name: "Ember Brazier",      icon: "🔥", category: "objects", price: 1400, reward: true, footprint: 0.55, blurb: "A stone bowl of deep orange-red fire." },
  brazierGilt:    { id: "brazierGilt",    name: "Gilt Brazier",       icon: "🔥", category: "objects", price: 1400, reward: true, footprint: 0.55, blurb: "A stone brazier with a warm gold-white flame." },
  // ── batch 3 ──
  // trees
  cherryTree:  { id: "cherryTree",  name: "Cherry Blossom", icon: "🌸", category: "trees", price: 16000, footprint: 1.0, blurb: "A cherry tree crowned in soft pink blossom." },
  poplarTree:  { id: "poplarTree",  name: "Lombardy Poplar",icon: "🌲", category: "trees", price: 3000,  footprint: 0.6, blurb: "A tall, slim columnar poplar reaching skyward." },
  willowTree:  { id: "willowTree",  name: "Weeping Willow", icon: "🌳", category: "trees", price: 7000,  footprint: 1.1, blurb: "A broad willow whose long foliage hangs almost to the ground." },
  // rocks / nature
  flatBoulder: { id: "flatBoulder", name: "Flat Boulder",   icon: "🪨", category: "rocks", price: 300, footprint: 0.8, blurb: "A low, flat-topped boulder." },
  // wood
  // RETHEMED, NOT RENAMED. This slot used to be the Garden Arch; it now draws a weather vane — a gilded
  // arrow on a tall iron mast. The KEY stays `gardenArch` on purpose — same reason as `scarecrow`,
  // `hammock`, `dovecote`, `beehive` and `coldFrame`: a decor id is what a save file stores for every
  // placed copy, so renaming it would orphan every existing placement. Only the presentation changed.
  // The FOOTPRINT shrank 1.2 → 0.42 with the subject: the arch was a two-frame structure you walked
  // UNDER, and a mast touches the world through one small stone footing. It moves `wood` → `objects`
  // (there is no timber left on it) and it is NOT a host and has no `host` field — the arch had none
  // either, despite the old blurb's "climbing greenery", so nothing was unwired here.
  // art: weather vane — the id is the old Garden Arch's; saves store ids, so it stays.
  gardenArch:  { id: "gardenArch",  name: "Weather Vane",   icon: "🧭", category: "objects", price: 3000, footprint: 0.42, blurb: "A gold arrow on a tall iron pole that swings with the wind." },
  barrel:      { id: "barrel",      name: "Oak Barrel",     icon: "🛢️", category: "wood",  price: 300, footprint: 0.72,blurb: "A banded oak barrel resting on its end." },
  // objects
  windChime:   { id: "windChime",   name: "Wind Chime",     icon: "🎐", category: "objects", price: 120, footprint: 0.35,blurb: "A hook post hung with softly chiming tubes." },
  armillary:   { id: "armillary",   name: "Armillary Sphere",icon: "🌐", category: "objects", price: 3000, footprint: 0.5,  blurb: "An astronomer's brass sphere of rings on a stone base." },
  wishingWell: { id: "wishingWell", name: "Wishing Well",   icon: "🪣", category: "objects", price: 3000, footprint: 0.95,blurb: "A stone wishing well with a little shingle roof." },
  // RETHEMED, NOT RENAMED. This slot used to be the Scarecrow; it now draws a snapped-off marble column.
  // The KEY stays `scarecrow` on purpose — same reason as `dovecote`, `beehive`, `coldFrame` and
  // `hammock`: a decor id is what a save file stores for every placed copy, so renaming it would orphan
  // every existing placement. Only the presentation changed. The FOOTPRINT grew 0.6 → 0.66 with the
  // subject: a stone drum on a plinth has a real footing where a post driven into the turf has none.
  // It stays in `objects` alongside the obelisk (the `rocks` category is natural, unworked stone).
  // art: broken marble column — the id is the old Scarecrow's; saves store ids, so it stays.
  scarecrow:   { id: "scarecrow",   name: "Broken Column",  icon: "🏛️", category: "objects", price: 300, footprint: 0.66, blurb: "A marble column snapped off partway up." },
  topiaryBall: { id: "topiaryBall", name: "Ball Topiary",   icon: "🌳", category: "objects", price: 650, footprint: 0.55,blurb: "A neatly clipped boxwood sphere on a stem." },
  topiarySpiral:{ id: "topiarySpiral", name: "Spiral Topiary", icon: "🌀", category: "objects", price: 1400, footprint: 0.55,blurb: "An elegant spiral-trimmed evergreen." },
  // RETHEMED, NOT RENAMED. This slot used to be the Hammock; it now draws a plain slatted wooden crate.
  // The KEY stays `hammock` on purpose — same reason as `dovecote`, `beehive` and `coldFrame` below: a
  // decor id is what a save file stores for every placed copy, so renaming it would orphan every existing
  // placement. Only the presentation changed. The FOOTPRINT shrank from 1.25 to 0.55 with the subject: a
  // crate is a compact box where the hammock was a seven-foot sling strung between two posts. It is NOT a
  // host and has no `host` field — the hammock had none either, so nothing was unwired here.
  // art: slatted wooden crate — the id is the old Hammock's; saves store ids, so it stays.
  hammock:     { id: "hammock",     name: "Wooden Crate",   icon: "📦", category: "wood",    price: 1400, footprint: 0.55,blurb: "A plain wooden shipping crate." },
  fairyLights: { id: "fairyLights", name: "Fairy Lights",   icon: "✨", category: "objects", price: 650, footprint: 0.7, blurb: "A pair of posts strung with warm glowing bulbs." },
  rainBarrel:  { id: "rainBarrel",  name: "Rain Barrel",    icon: "🛢️", category: "objects", price: 300, footprint: 0.66, blurb: "A wooden rain barrel catching water off a spout." },
  logStore:    { id: "logStore",    name: "Log Store",      icon: "🪵", category: "wood",    price: 1400, footprint: 0.85, blurb: "An open-front wooden shelter stacked with split logs." },
  gazingBall:  { id: "gazingBall",  name: "Gazing Ball",    icon: "🔮", category: "objects", price: 1400, footprint: 0.5, blurb: "A mirrored glass ball on a stone stem." },
  // RETHEMED, NOT RENAMED. This slot used to be the Dovecote; it now draws a round hay bale. The KEY stays
  // `dovecote` on purpose — same reason as `beehive` and `coldFrame` below: a decor id is what a save file
  // stores for every placed copy, so renaming it would orphan every existing placement. Only the
  // presentation changed. The FOOTPRINT grew from 0.55 to 0.8 with the subject: a bale lying on its side
  // is a wide, low object where the pigeon house was a slim post.
  // art: round hay bale — the id is the old Dovecote's; saves store ids, so it stays.
  dovecote:    { id: "dovecote",    name: "Hay Bale",       icon: "🛞", category: "objects", price: 3000, footprint: 0.8, blurb: "A big round hay bale lying on its side." },
  // RETHEMED, NOT RENAMED. This slot used to be the Beehive; it now draws a trash bin. The KEY stays
  // `beehive` on purpose — same reason as `coldFrame` below: a decor id is what a save file stores for
  // every placed copy, so renaming it would orphan every existing placement. Only the presentation changed.
  // art: trash bin (timber bin store) — the id is the old Beehive's; saves store ids, so it stays.
  beehive:     { id: "beehive",     name: "Trash Bin",      icon: "🗑️", category: "objects", price: 1400, footprint: 0.5, blurb: "A wooden bin with a black liner and trash poking out the top." },
  // RETHEMED, NOT RENAMED. This slot used to be the Cold Frame; it now draws a seed bag. The KEY stays
  // `coldFrame` on purpose — a decor id is what a save file stores for every placed copy, so renaming it
  // would orphan every existing placement in every save. Only the presentation changed.
  // art: seed bag — the id is the old Cold Frame's; saves store ids, so it stays.
  coldFrame:   { id: "coldFrame",   name: "Seed Bag",       icon: "🌾", category: "objects", price: 650, footprint: 0.45, blurb: "A burlap sack of seed, rolled open with a little spilled at the bottom." },
  waterPump:   { id: "waterPump",   name: "Water Pump",     icon: "🚰", category: "objects", price: 1400, footprint: 0.5, blurb: "A cast-iron pump with a long curved handle over a stone trough." },
  firePit:     { id: "firePit",     name: "Fire Pit",       icon: "🔥", category: "objects", price: 1400, footprint: 0.62, blurb: "A ring of stones around embers, throwing warm light across the ground." },
  pottingBench:{ id: "pottingBench",name: "Potting Bench",  icon: "🪴", category: "objects", price: 1400, footprint: 0.85, blurb: "A wooden work table covered in pots and tools." },
  // ── REWARD-ONLY, PER-SPECIES (achievement rewards — sim/speciesDecor.ts). Price 0: earned, not bought,
  //    so they have no sell-back. Each copy carries the species it honours. ──
  botanicalMarker: { id: "botanicalMarker", name: "Botanical Marker", icon: "🏷️", category: "objects", price: 0, footprint: 0.34, reward: true, perSpecies: true,
    blurb: "A slate label in a brass frame on an iron stake, showing the species in its true colors." },
  goldStatue: { id: "goldStatue", name: "Gold Statue", icon: "🏆", category: "objects", price: 0, footprint: 0.5, reward: true, perSpecies: true,
    blurb: "The species cast in gold on a stone base. You get one by reaching the top rung." },
  // ── KICKSTARTER BACKER pieces — granted first (a code or the admin panel; see DecorDef.backer), then buyable at
  //    this price by whoever was granted one. 3000 = the top of the brazier family (the Spectral Brazier). ──
  foundersBrazier: { id: "foundersBrazier", name: "Founders' Brazier", icon: "🔥", category: "objects", price: 3000, footprint: 0.55, backer: true,
    blurb: "A black chalice banded in gold on a stone pedestal, its fire shifting color. A thank-you to Shelf Garden's Kickstarter backers." },
};

export const DECOR_IDS: DecorId[] = Object.keys(DECOR) as DecorId[];
export const DECOR_CATEGORIES: { id: DecorCategory; label: string }[] = [
  { id: "trees", label: "Trees" },
  { id: "planters", label: "Planters" },
  { id: "rocks", label: "Rocks" },
  { id: "wood", label: "Wood" },
  { id: "objects", label: "Objects" },
];

export function decorDef(id: DecorId): DecorDef { return DECOR[id]; }
/** The EFFECTIVE coin price for a decor item: a live `decorPrices` override → the static def price. (The dev
 *  tier tag never sets a price.) All shop/buy paths should use THIS (see sim/decorPricing.ts). */
export function decorPrice(id: DecorId): number { return effectiveDecorPrice(id, DECOR[id]?.price ?? 0); }
/** What selling a decor item back pays, as a fraction of what it currently costs to buy. HALF: enough that
 *  undoing a purchase is a real option rather than a write-off, low enough that buying and re-selling is a
 *  loss and never a way to launder coins. THE SINGLE KNOB — nothing else should hardcode a refund fraction. */
export const DECOR_REFUND_RATE = 0.5;
/** Coins paid for selling one of `id` back. Priced off the LIVE price (`decorPrice`), not the static def
 *  number, so a tuning-tool price edit moves the buy and the sell-back together — otherwise an admin raising
 *  a price would leave an item that refunds more than the next one costs. */
export function decorSellValue(id: DecorId): number {
  return Math.floor(decorPrice(id) * DECOR_REFUND_RATE);
}
export function isDecorId(s: string): s is DecorId { return Object.prototype.hasOwnProperty.call(DECOR, s); }
/** Is this an ACHIEVEMENT REWARD item? Earned FIRST: an achievement grants one copy, and only then does the
 *  shop list it so more can be bought (decorByCategory's `open`). A reward item that is also `perSpecies` is a
 *  TROPHY — see `isTrophy`. */
export function isRewardItem(id: DecorId): boolean { return DECOR[id]?.reward === true; }
/** A TROPHY (the per-species Botanical Marker / Gold Statue): granted once by its achievement and NEVER
 *  obtainable again — so never listed in the shop, never sellable, and never removable from the bag. */
export function isTrophy(id: DecorId): boolean { return DECOR[id]?.perSpecies === true; }
/** A KICKSTARTER BACKER piece (DecorDef.backer): granted first (a code, the admin panel), then BUYABLE by whoever
 *  was granted one — listed in the shop once its "decor:<id>" unlock is open (App's cosmeticAccess). Never sold
 *  back: the granted copy is a thank-you, not coins. */
export function isBackerItem(id: DecorId): boolean { return DECOR[id]?.backer === true; }
/** NEVER SOLD BACK: a trophy or a backer piece — the test every SELL path asks. (A backer piece can be BOUGHT once
 *  granted; a trophy never can — see `decorBuyable`.) */
export function isKeepsake(id: DecorId): boolean { return isTrophy(id) || isBackerItem(id); }
/** May a copy be BOUGHT at all? Not a trophy (granted once, never again) and not a free item (a 0 price would hand
 *  out copies). Whether it is LISTED for this player is `decorByCategory`'s `open`. */
export function decorBuyable(id: DecorId): boolean { return !isTrophy(id) && decorPrice(id) > 0 && isIn(DECOR[id]); }   // (a content release that is not out yet — sim/releases.ts)
/** The decor the coin shop lists UNCONDITIONALLY: everything not marked as a reward item or a keepsake. (Reward
 *  items join the listing only once earned — decorByCategory's `open`; keepsakes never do.) */
export const SHOP_DECOR_IDS: DecorId[] = DECOR_IDS.filter((id) => !isRewardItem(id) && !isKeepsake(id));
// (a backer piece is not in it: it is listed only for players granted one — decorByCategory's `open`)
/** Every Kickstarter backer piece — the decor a backer code hands out (and the admin Codes page flags). */
export const BACKER_DECOR_IDS: DecorId[] = DECOR_IDS.filter((id) => isBackerItem(id));
/** The decor a fish / insect SPAWN CONDITION may name (the odds editors' suggestions; decorConds.test.ts pins
 *  that the shipped conditions keep to it). A backer piece is never one: a creature that came only to a backer's
 *  garden would turn a thank-you into a gameplay advantage. */
export const SPAWN_DECOR_IDS: DecorId[] = DECOR_IDS.filter((id) => !isBackerItem(id));
/** Every decor item that IS an achievement reward — the pool the reward editor may grant from. */
export const REWARD_DECOR_IDS: DecorId[] = DECOR_IDS.filter((id) => isRewardItem(id));
/** A decor item's display name, or the RAW ID when it is not in the catalogue. The fallback is the point:
 *  ids reach here from authored rewards and untrusted tuning JSON, so a retired or mistyped one must read as
 *  the thing an editor has to go fix rather than as a blank. Player-facing summaries use it too, which is why
 *  it lives here and not in the dev editor. */
export function decorName(id: string): string { return isDecorId(id) ? DECOR[id].name : id; }
/**
 * The SHOP listing for one category — the function the decor shop builds its tabs from, so filtering here is
 * what makes `reward: true` mean "earn it before you can buy it". Reward items are listed only once `open(id)`
 * says the player has earned them (their achievement unlocks "decor:<id>" — sim/cosmetics.ts decorOpen); a
 * Kickstarter BACKER piece the same way, once they were granted one (App opens "decor:<id>" from the grant ledger);
 * trophies never are — not even with the dev override on. Dev/analysis tools that need the full catalogue should
 * iterate `DECOR_IDS` directly.
 */
export function decorByCategory(cat: DecorCategory, open: (id: DecorId) => boolean = () => false): DecorDef[] {
  return DECOR_IDS.map((id) => DECOR[id]).filter((d) => d.category === cat && !isTrophy(d.id) && isIn(d)   // (a content release that is not out yet — sim/releases.ts)
    && ((!d.reward && !d.backer) || open(d.id)));
}
/** The shop's "👀 Coming soon" cards for one category: shop decor whose release is in preview (sim/releases.ts
 *  isPreview) — shown locked, never sold. Reward, backer and trophy pieces are never teased. */
export function decorTeasers(cat: DecorCategory): DecorDef[] {
  return DECOR_IDS.map((id) => DECOR[id]).filter((d) => d.category === cat && !isTrophy(d.id) && !d.reward && !d.backer
    && isPreview(d));
}

// ─────────────────────────────────────── COLLISION / OCCLUSION BANDS ───────────────────────────────────────
/**
 * The band is what crawling insects collide with + what occludes fliers (see GardenGroundLayer.drawInsects +
 * InsectLayer.crawlerBlocked). Values are si-FRACTIONS (height above the base) so they're RESIZE-SAFE.
 *
 * `decorBand(id)` returns the authored `DECOR[id].band` if present, else an AUTO-DEFAULT so EVERY item (incl.
 * future ones) is handled. The auto-default is derived, in priority order, from:
 *   1. `BAND_DEFAULT_SILHOUETTE` below — the hand-tuned tall-item silhouettes (migrated from the old
 *      GardenGroundLayer DECOR_OCCLUDER table, so existing occlusion is preserved), expressed as a band.
 *   2. trees (`id` ends with "Tree") → a tall tapering cone (canopy ≈ 3.6·si, ≈1.05·si half-wide).
 *   3. everything else → a short ground box sized off the item's `footprint` (so short/flat decor still has a
 *      small base collision matching its footprint, and never occludes — it's too short to reach a flier).
 */

/** Tall-item silhouettes (si-fractions) that meaningfully occlude/collide. `h` = top height above base,
 *  `w` = half-width. Migrated from the render-layer DECOR_OCCLUDER so behaviour is unchanged until items are
 *  authored a bespoke `band`. Short/flat decor (rocks, benches, logs) is intentionally ABSENT here → it gets
 *  the footprint-box default (a small base collision, no occlusion). */
const BAND_DEFAULT_SILHOUETTE: Partial<Record<DecorId, { h: number; w: number }>> = {
  birdBath:      { h: 1.0,  w: 0.92 },
  stoneUrn:      { h: 1.05, w: 0.62 },
  planterBox:    { h: 0.85, w: 0.7 },
  hangingBasket: { h: 1.5,  w: 0.55 },
  lantern:       { h: 1.15, w: 0.42 },
  signpost:      { h: 1.0,  w: 0.5 },
  obelisk:       { h: 1.35, w: 0.42 },
  sundial:       { h: 0.8,  w: 0.5 },
  fountain:      { h: 1.05, w: 0.75 },
  wishingWell:   { h: 1.1,  w: 0.7 },
  gardenArch:    { h: 1.29, w: 0.13 },   // RETHEMED to a WEATHER VANE: a mast, not a two-frame arbour
  armillary:     { h: 1.15, w: 0.5 },
  windChime:     { h: 1.4,  w: 0.4 },
  torch:         { h: 1.5,  w: 0.35 },
  birdhouse:     { h: 1.4,  w: 0.42 },
  barrel:        { h: 0.9,  w: 0.55 },
};

/** Convert an item's `footprint` (garden-unit radius) into a rough si-fraction half-width for the base box
 *  default. DECOR_S=74 screen-px per unit; iso projection makes a garden-unit radius ≈ tileW(46) screen-px
 *  wide, so half-width_px ≈ footprint·46, and si-fraction ≈ that / si. Using si≈74 (scale 1) as the reference
 *  keeps this a stable default; authored bands supersede it. Clamped so tiny items still get a token width. */
function footprintBandHalfW(footprint: number): number {
  return Math.max(0.14, Math.min(1.2, (footprint * 46) / 74));
}

/**
 * AUTHORED BANDS — hand-tuned in the collision editor (viewer/in-app admin) and committed here. These are the
 * source-of-truth bands that ship; the resolver prefers them over the auto-defaults. Edit these values (or
 * re-author in the editor and paste) to change collision/occlusion. A LIVE override from the server can also
 * layer on top at runtime (see setDecorBandOverrides).
 */
const AUTHORED_BANDS: Partial<Record<DecorId, DecorBand>> = {
  pebbles: { top: 0.387, bottom: 0, profile: [0, 0, 0.176, 0.23, 0.257, 0.149] },
  rock: { top: 0.385, bottom: -0.054, profile: [0.392, 0.419, 0.419, 0.419, 0.378, 0.095] },
  mossyRock: { top: 0.32, bottom: -0.054, profile: [0.432, 0.432, 0.432, 0.432, 0.405, 0.108] },
  boulder: { top: 0.458, bottom: -0.135, profile: [0.618, 0.637, 0.647, 0.637, 0.589, 0.077] },
  stump: { top: 0.327, bottom: -0.064, profile: [0.326, 0.405, 0.405, 0.405, 0.405, 0.095] },
  log: { top: 0.326, bottom: 0, profile: [0.667, 0.676, 0.676, 0.676, 0.667, 0.591] },
  lantern: { top: 0.101, bottom: 0, profile: [0.109, 0.114, 0.151, 0.161, 0.161, 0] },
  signpost: { top: 0.111, bottom: 0, profile: [0.062, 0.062, 0.062, 0.062, 0.062, 0] },
  birdBath: { top: 0.168, bottom: -0.087, profile: [0.187, 0.254, 0.301, 0.301, 0.261, 0.06] },
  planterBox: { top: 0.183, bottom: -0.016, profile: [0.453, 0.453, 0.453, 0.453, 0.453, 0.453] },
  hangingBasket: { top: 0.089, bottom: -0.014, profile: [0.043, 0.043, 0.043, 0.142, 0.142, 0.135] },
  stoneUrn: { top: 0.132, bottom: -0.038, profile: [0.075, 0.098, 0.18, 0.203, 0.18, 0.038] },
  oakTree: { top: 0.067, bottom: -0.081, profile: [0.126, 0.13, 0.13, 0.133, 0.13, 0.042] },
  pineTree: { top: 0.173, bottom: -0.048, profile: [0.682, 0.283, 0.183, 0.1, 0.08, 0.084] },
  birchTree: { top: 0.059, bottom: -0.071, profile: [0.097, 0.097, 0.097, 0.097, 0.1, 0.037] },
  mapleTree: { top: 0.117, bottom: -0.083, profile: [0.19, 0.196, 0.2, 0.207, 0.214, 0.041] },
  rockCairn: { top: 0.137, bottom: -0.074, profile: [0.291, 0.324, 0.331, 0.311, 0.243, 0.095] },
  birdhouse: { top: 0.074, bottom: 0, profile: [0.039, 0.039, 0.039, 0.039, 0.039, 0] },
  woodpile: { top: 0.295, bottom: 0, profile: [0.526, 0.555, 0.548, 0.533, 0.505, 0] },
  sundial: { top: 0.095, bottom: -0.049, profile: [0.159, 0.173, 0.201, 0.194, 0.159, 0.028] },
  wheelbarrow: { top: 0.19, bottom: -0.006, profile: [0.47, 0.482, 0.605, 0.599, 0.452, 0.382] },
  pinwheel: { top: 0.063, bottom: 0, profile: [0.014, 0.014, 0.014, 0.014, 0.014, 0] },
  wateringCan: { top: 0.119, bottom: 0, profile: [0.191, 0.199, 0.199, 0.199, 0.119, 0] },
  gardenBench: { top: 0.143, bottom: 0, profile: [0.454, 0.391, 0.391, 0.391, 0.391, 0] },
  torch: { top: 0.082, bottom: 0, profile: [0.035, 0.035, 0.035, 0.035, 0.035, 0] },
  obelisk: { top: 0.12, bottom: 0, profile: [0.154, 0.154, 0.154, 0.203, 0.203, 0] },
  fountain: { top: 0.243, bottom: -0.116, profile: [0.162, 0.405, 0.459, 0.459, 0.394, 0.097] },
  cherryTree: { top: 0.104, bottom: -0.081, profile: [0.174, 0.178, 0.181, 0.185, 0.189, 0.054] },
  poplarTree: { top: 0.127, bottom: -0.048, profile: [0.083, 0.083, 0.083, 0.083, 0.083, 0.083] },
  flatBoulder: { top: 0.236, bottom: -0.023, profile: [0.345, 0.36, 0.405, 0.435, 0.443, 0.158] },
  // gardenArch: RETHEMED to a WEATHER VANE (see the def above). The old band was the ARCH'S OPENING — a
  // flat 0.719 half-width for its top half stepping to 0.421 — which is a structure a crawler walks
  // through, two frames wide. A mast is the opposite kind of object: it is a 0.018 bar standing on a
  // 0.128 stone footing, so the profile is authored to that silhouette, the footing's flare landing on
  // the ground row. `top` is the CARDINAL CROSS, not the arrow: the arrow is the item's widest run
  // (0.70·s across) but it rides at -1.16·s where nothing collides with it, and authoring the band off it
  // would give a garden ornament the collision radius of a mature tree.
  gardenArch: { top: 0.898, bottom: 0, profile: [0.2, 0.017, 0.017, 0.018, 0.056, 0.128] },
  barrel: { top: 0.146, bottom: -0.024, profile: [0.3, 0.294, 0.288, 0.282, 0.276, 0.041] },
  windChime: { top: 0.078, bottom: -0.006, profile: [0.031, 0.031, 0.031, 0.123, 0.123, 0.117] },
  armillary: { top: 0.117, bottom: -0.034, profile: [0.095, 0.135, 0.155, 0.169, 0.149, 0.068] },
  wishingWell: { top: 0.268, bottom: -0.017, profile: [0.265, 0.282, 0.282, 0.282, 0.282, 0.062] },
  // scarecrow: RETHEMED to a BROKEN COLUMN (see the def above). The old band was a HUMAN FIGURE reduced to
  // its post — a flat 0.03 half-width all the way up, i.e. the cross-frame's timber and nothing else, so
  // the shirt and the outstretched arms collided with nothing. A column is a solid stone drum, so the
  // profile is authored to the drawn silhouette: the shaft's s*0.132 → s*0.150 entasis up the run, then
  // the plinth and the rubble at its foot on the ground row. `top` is the highest STUB of the fracture,
  // not a level crown — the break is part of the silhouette.
  scarecrow: { top: 0.581, bottom: -0.012, profile: [0.132, 0.136, 0.143, 0.147, 0.15, 0.28] },
  topiaryBall: { top: 0.183, bottom: 0, profile: [0.154, 0.116, 0.122, 0.135, 0.142, 0] },
  topiarySpiral: { top: 0.143, bottom: 0, profile: [0.241, 0.112, 0.118, 0.123, 0.129, 0] },
  // hammock: RETHEMED to a WOODEN CRATE (see the def above). The old band described a sling hung between
  // two posts — a wide near-constant 0.44 profile with a hole in the middle of it. A crate is a solid box
  // that is widest at the deck and steps in below, so the profile is authored to the drawn silhouette:
  // the deck diamond at the top, then the box’s parallel sides, which are narrower in screen x than the
  // deck because the deck’s far corner reaches back behind them.
  hammock: { top: 0.115, bottom: -0.012, profile: [0.26, 0.30, 0.30, 0.30, 0.30, 0.28] },
  fairyLights: { top: 0.094, bottom: 0, profile: [0.449, 0.449, 0.449, 0.449, 0.449, 0] },
  rainBarrel: { top: 0.203, bottom: -0.006, profile: [0.293, 0.287, 0.282, 0.315, 0.276, 0.09] },
  // windowBox: the auto-default (0.5 tall, 0.53 half-wide off `footprint`) is wrong for a LONG LOW shape in
  // both directions at once — too tall by a fifth and barely two-thirds as wide as the trough really is, so
  // crawlers would walk through its ends and bump into air above its rim. Authored to the drawn silhouette.
  //
  // RE-AUTHORED WITH THE RETHEME (brackets → legs): `top` 0.445 → 0.50 because the box now stands higher,
  // and the lower half of the profile changed KIND. It used to step in to two solid bracket triangles that
  // ran all the way to the turf; the item now stands on four thin legs with OPEN AIR between them, and the
  // samples below the floor line (0.245) narrow hard to the leg pair rather than staying near-solid. The
  // top three samples (0.50/0.40/0.30) are the timber body with its slight batter; the bottom three
  // (0.20/0.10/0) are the leg zone, held at the legs' own outer reach (legX 0.60 + half-width) so a crawler
  // walks into a leg but not through the gap where there is nothing to hit.
  windowBox: { top: 0.50, bottom: 0, profile: [0.763, 0.752, 0.738, 0.638, 0.638, 0.646] },
};

/** LIVE overrides pushed from the server (admin edits), layered ON TOP of AUTHORED_BANDS at runtime. Empty
 *  until the game fetches them. Set via setDecorBandOverrides; cleared by passing {}. */
let bandOverrides: Partial<Record<DecorId, DecorBand>> = {};
/** Install live band overrides (from the server's admin-edited set). Also busts the band-radius cache. */
export function setDecorBandOverrides(o: Partial<Record<DecorId, DecorBand>> | null | undefined): void {
  bandOverrides = o && typeof o === "object" ? o : {};
  bandRadiusCache.clear();
}

/** The resolved collision/occlusion band for a decor id (live override → authored → auto-default). Non-null. */
/** True if this decor item has a REAL authored band (a committed AUTHORED_BANDS entry, a DECOR[id].band, or a
 *  live server override) — as opposed to a derived auto-default. The band editor uses this so it loads the
 *  committed values verbatim instead of re-clamping the top line to the sampled silhouette. */
export function hasAuthoredBand(id: DecorId): boolean {
  return !!(bandOverrides[id] || DECOR[id]?.band || AUTHORED_BANDS[id]);
}

/** The live band overrides currently installed (id → band). Read by the dev override report + band editors. */
export function getDecorBandOverrides(): Partial<Record<DecorId, DecorBand>> { return bandOverrides; }

export function decorBand(id: DecorId): DecorBand {
  return bandOverrides[id] ?? authoredDecorBand(id);
}

/** The band the BUNDLE ships for an id, ignoring any live override: authored → auto-default. */
export function authoredDecorBand(id: DecorId): DecorBand {
  const authored = DECOR[id]?.band ?? AUTHORED_BANDS[id];
  if (authored) return authored;
  const sil = BAND_DEFAULT_SILHOUETTE[id];
  if (sil) return { top: sil.h, bottom: 0.04, profile: [sil.w, sil.w] };
  if (id.endsWith("Tree")) {
    // a tapering cone: wide near the base, narrowing to the apex (matches the old sceneTrees tree estimate).
    return { top: 3.6, bottom: 0.02, profile: [0.30, 0.62, 0.9, 1.05] };
  }
  const fp = DECOR[id]?.footprint ?? 0.4;
  const w = footprintBandHalfW(fp);
  return { top: 0.5, bottom: 0.0, profile: [w, w] };
}

/** Half-width (si-fraction) of a band at normalized depth `t` (0 = top line, 1 = bottom line), by linearly
 *  interpolating the `profile` samples. Used by both the occlusion silhouette and crawler collision. */
export function bandHalfWidthAt(band: DecorBand, t: number): number {
  const p = band.profile;
  if (p.length === 0) return 0;
  if (p.length === 1) return p[0]!;
  const tt = Math.max(0, Math.min(1, t));
  const f = tt * (p.length - 1);
  const i = Math.floor(f);
  if (i >= p.length - 1) return p[p.length - 1]!;
  const frac = f - i;
  return p[i]! * (1 - frac) + p[i + 1]! * frac;
}

/** The WIDEST half-width in a band's LOWER region (its ground footprint), as an si-fraction. Crawlers collide
 *  with the near-ground part of the shape, so this drives the crawler collision radius. */
export function bandGroundHalfWidth(band: DecorBand): number {
  // sample the lower ~35% of the band (nearest the ground) and take the max half-width there.
  let maxW = 0;
  for (let k = 0; k <= 6; k++) {
    const t = 0.65 + (k / 6) * 0.35;   // 0.65..1.0 → lower region
    maxW = Math.max(maxW, bandHalfWidthAt(band, t));
  }
  return maxW;
}

const bandRadiusCache = new Map<DecorId, number>();
// a live decorArtScale tuning change invalidates the cached collision radii (they depend on the scale).
onTuning(() => bandRadiusCache.clear());
/**
 * The crawler-collision RADIUS (garden units) for a decor item, derived from its band's ground silhouette.
 * The band's lower half-width (si-fraction) → screen px (× si at the item's DEFAULT scale) → garden units via
 * the iso inverse (a horizontal screen span maps to a garden distance). This replaces the flat `footprint`
 * circle with a radius that matches the drawn base shape; the shadow is excluded (band bottom ≥ 0). Cached.
 */
export function decorBandGardenRadius(id: DecorId): number {
  const hit = bandRadiusCache.get(id);
  if (hit !== undefined) return hit;
  const band = decorBand(id);
  // LIVE decor art scale (tuning override ?? default) so crawler collision matches a live-resized item; the
  // cache is busted on any tuning change (see the onTuning subscriber below).
  const si = DECOR_S * tuning<number>(`decorArtScale.${id}`, DECOR_ART_SCALE[id] ?? 1);
  const halfPx = bandGroundHalfWidth(band) * si;         // screen-px half-width at the ground
  // a purely-horizontal screen span (halfPx, 0) → garden-space distance from the base.
  const gp = screenToIso(halfPx, 0);
  const r = Math.hypot(gp.x, gp.y);
  bandRadiusCache.set(id, r);
  return r;
}

/** The resolved band for EVERY decor id (live override → authored → default). For the editor + JSON download. */
export function allDecorBands(): Record<DecorId, DecorBand> {
  const out = {} as Record<DecorId, DecorBand>;
  for (const id of DECOR_IDS) out[id] = decorBand(id);
  return out;
}

/** Defensive coerce of a persisted/served band MAP (decorId → {top,bottom,profile}). Drops bad entries so a
 *  malformed server payload can never crash the game. Returns only valid, known-id bands. */
export function coerceDecorBands(raw: unknown): Partial<Record<DecorId, DecorBand>> {
  const out: Partial<Record<DecorId, DecorBand>> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!isDecorId(k) || !v || typeof v !== "object") continue;
    const o = v as Record<string, unknown>;
    const top = o.top, bottom = o.bottom, profile = o.profile;
    if (typeof top !== "number" || !Number.isFinite(top)) continue;
    if (typeof bottom !== "number" || !Number.isFinite(bottom)) continue;
    if (!Array.isArray(profile) || profile.length < 1 || !profile.every((n) => typeof n === "number" && Number.isFinite(n))) continue;
    out[k] = { top, bottom, profile: profile as number[] };
  }
  return out;
}

/** Max characters per line of signpost text (kept short so it fits the board without squishing). */
export const SIGN_LINE_MAX = 12;

/** One placed decor instance in the garden. `id` is a stable unique key. `seed` individualises the
 *  procedural art (rocks/pebbles vary by it); undefined = a default look. Seeds are kept for PLACED
 *  items but NOT for bagged (picked-up) ones — placing from the bag re-rolls a fresh appearance.
 *  `text` is the two-line label on a SIGNPOST (clamped to SIGN_LINE_MAX per line).
 *  `flip` mirrors the item left-to-right (right-click toggles it); absent = unflipped, which is what every
 *  save written before the bit existed loads as. Stored only when true. Read it through `decorFlipped()`. */
export interface DecorPlacement {
  id: string;
  decor: DecorId;
  gx: number;
  gy: number;
  seed?: number;
  text?: { l1: string; l2: string };
  flip?: boolean;
  /** PER-SPECIES items only (`DecorDef.perSpecies`): the species it honours, as "plant:rose" /
   *  "fish:koi" / "insect:monarch" — the kind is part of the ref because fish and insect ids overlap. */
  species?: string;
}

/** A species ref — "plant:rose", "fish:koi", "insect:monarch". */
export const SPECIES_REF_RE = /^(plant|fish|insect):[A-Za-z0-9_]+$/;
/** THE BAG KEY of a decor item: the plain id, or "id:kind:species" for a per-species one. `decorOwned` is
 *  keyed by it and `availableDecor` counts placements by it, so a Gold Rose Statue and a Gold Koi Statue are
 *  different things in the bag while sharing one catalogue entry. */
export function decorKey(decor: DecorId, species?: string): string {
  return species && DECOR[decor]?.perSpecies ? `${decor}:${species}` : decor;
}
/** A placement's bag key. */
export function placementKey(p: { decor: DecorId; species?: string }): string { return decorKey(p.decor, p.species); }
/** Split a bag key back into its decor id and species (undefined for an ordinary item); null if malformed. */
export function parseDecorKey(key: string): { decor: DecorId; species?: string } | null {
  const i = key.indexOf(":");
  if (i < 0) return isDecorId(key) ? { decor: key } : null;
  const decor = key.slice(0, i), species = key.slice(i + 1);
  if (!isDecorId(decor) || !DECOR[decor].perSpecies || !SPECIES_REF_RE.test(species)) return null;
  return { decor, species };
}

/** The keys of an authored decor grant (`AchievementReward.decor`) that the grant path would SKIP — a
 *  retired or mistyped id, or a malformed per-species key. Uses `parseDecorKey`, the exact rule the grant
 *  (sim/speciesDecor.ts decorGrants) and the bag loader use, so a valid trophy key ("goldStatue:plant:rose")
 *  is not reported; `isDecorId` alone would flag every one. Both reward editors call this. */
export function unknownDecorKeys(decor: Readonly<Record<string, unknown>> | undefined): string[] {
  return Object.keys(decor ?? {}).filter((k) => !parseDecorKey(k));
}

/** Clamp a raw string to one sign line (trim + cap length). */
export function clampSignLine(s: string): string {
  return String(s ?? "").replace(/\s+/g, " ").trimStart().slice(0, SIGN_LINE_MAX);
}

/** Defensive coerce of a persisted decor-placements array (save + server). A placement whose id has left
 *  `DECOR` is DROPPED — a retired item vanishes from the garden with no refund. See the retirement note above
 *  `DECOR`: retiring an id needs its own load migration. */
export function coerceDecorPlacements(raw: unknown): DecorPlacement[] {
  if (!Array.isArray(raw)) return [];
  const out: DecorPlacement[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const decor = typeof o.decor === "string" && isDecorId(o.decor) ? o.decor : null;
    const gx = typeof o.gx === "number" ? o.gx : null;
    const gy = typeof o.gy === "number" ? o.gy : null;
    if (decor == null || gx == null || gy == null) continue;
    const id = typeof o.id === "string" && o.id ? o.id : `dp_${out.length}`;
    const seed = typeof o.seed === "number" && Number.isFinite(o.seed) ? o.seed : undefined;
    let text: { l1: string; l2: string } | undefined;
    if (o.text && typeof o.text === "object") {
      const tt = o.text as Record<string, unknown>;
      const l1 = clampSignLine(typeof tt.l1 === "string" ? tt.l1 : "");
      const l2 = clampSignLine(typeof tt.l2 === "string" ? tt.l2 : "");
      if (l1 || l2) text = { l1, l2 };
    }
    const flip = o.flip === true;
    const species = DECOR[decor].perSpecies && typeof o.species === "string" && SPECIES_REF_RE.test(o.species) ? o.species : undefined;
    // a per-species item with no species honours nothing — drop it rather than draw an empty plinth
    if (DECOR[decor].perSpecies && !species) continue;
    out.push({ id, decor, gx, gy, ...(seed !== undefined ? { seed } : {}), ...(text ? { text } : {}), ...(flip ? { flip } : {}), ...(species ? { species } : {}) });
  }
  return out;
}

/** Defensive coerce of a persisted owned-decor count map (decorId → count). An unknown id is DROPPED — a
 *  retired item silently leaves the player's bag. See the retirement note above `DECOR`. */
export function coerceDecorOwned(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    // plain ids, and per-species bag keys ("goldStatue:plant:rose") — see decorKey
    if (parseDecorKey(k) && typeof v === "number" && v > 0) out[k] = Math.floor(v);
  }
  return out;
}

/** How many of `decor` are currently PLACED. */
export function placedDecorCount(placements: DecorPlacement[], key: string): number {
  let n = 0;
  for (const p of placements) if (placementKey(p) === key) n++;
  return n;
}
/** How many of `decor` are OWNED but not yet placed (available to place). */
/** How many of `key` (a plain decor id, or a per-species bag key — see decorKey) are OWNED but not placed.
 *  (The dev override's view of the bag is sim/speciesDecor.ts decorStockView.) */
export function availableDecor(owned: Record<string, number>, placements: DecorPlacement[], decor: string): number {
  return Math.max(0, (owned[decor] ?? 0) - placedDecorCount(placements, decor));
}
